import React, { useState, useCallback, useMemo } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { databaseKeys } from '@/hooks/useDatabase'
import { useFormValidation } from '@/hooks/useFormValidation'
import {
  ValidatedInput,
  ValidationSummary,
  FormSection,
  FormActions
} from '@/components/common/ValidatedForm'
import { delay } from '@/utils/delay'

interface Column {
  id: string
  name: string
  dataType: string
  length?: number
  nullable: boolean
  primaryKey: boolean
  autoIncrement: boolean
  defaultValue?: string
  unique: boolean
  comment?: string
}

interface CreateTableViewProps {
  connectionId: string
  database: string
  schema?: string
  onTableCreated?: (tableName: string) => void
  onCancel?: () => void
}

const DATA_TYPES = [
  'INT',
  'BIGINT',
  'SMALLINT',
  'TINYINT',
  'DECIMAL',
  'FLOAT',
  'DOUBLE',
  'VARCHAR',
  'CHAR',
  'TEXT',
  'LONGTEXT',
  'DATE',
  'DATETIME',
  'TIMESTAMP',
  'TIME',
  'BOOLEAN',
  'BLOB',
  'JSON'
]

export function CreateTableView({ connectionId, database, schema, onTableCreated, onCancel }: CreateTableViewProps) {
  const [tableName, setTableName] = useState('')
  const [columns, setColumns] = useState<Column[]>([
    {
      id: '1',
      name: 'id',
      dataType: 'INT',
      nullable: false,
      primaryKey: true,
      autoIncrement: true,
      unique: false
    }
  ])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  // Form validation setup
  const { 
    validateField,
    validateForm,
    clearValidation,
    errors,
    warnings
  } = useFormValidation(
    { 
      type: 'mysql', 
      id: connectionId,
      name: '',
      host: '',
      port: 3306,
      username: '',
      password: '',
      createdAt: '',
      updatedAt: ''
    }, // Mock connection for validation
    [] // No existing names needed
  )

  const [columnErrors, setColumnErrors] = useState<Record<string, string>>({})

  const validateTableName = useCallback((name: string) => {
    void validateField('tableName', name, {
      required: true,
      type: 'identifier',
      maxLength: 64
    })
  }, [validateField])

  const validateColumnName = useCallback((columnId: string, name: string) => {
    if (!name.trim()) {
      setColumnErrors(prev => ({ ...prev, [columnId]: 'Column name is required' }))
      return false
    }
    
    // Check for duplicate column names
    const duplicates = columns.filter(col => col.id !== columnId && col.name.toLowerCase() === name.toLowerCase())
    if (duplicates.length > 0) {
      setColumnErrors(prev => ({ ...prev, [columnId]: 'Column name must be unique' }))
      return false
    }
    
    // Validate identifier format
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
      setColumnErrors(prev => ({ ...prev, [columnId]: 'Invalid column name format' }))
      return false
    }
    
    // Clear error if valid
    setColumnErrors(prev => {
      const newErrors = { ...prev }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [columnId]: _, ...rest } = newErrors
      return rest
    })
    return true
  }, [columns])

  const addColumn = () => {
    const newColumn: Column = {
      id: Date.now().toString(),
      name: '',
      dataType: 'VARCHAR',
      length: 255,
      nullable: true,
      primaryKey: false,
      autoIncrement: false,
      unique: false
    }
    setColumns(Array.from(columns).concat(newColumn))
  }

  const removeColumn = (columnId: string) => {
    setColumns(columns.filter(col => col.id !== columnId))
    // Clear validation errors for removed column
    setColumnErrors(prev => {
      const newErrors = { ...prev }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [columnId]: _, [`${columnId}_length`]: __, ...rest } = newErrors
      return rest
    })
  }

  const updateColumn = (columnId: string, field: keyof Column, value: Column[keyof Column]) => {
    setColumns(columns.map(col => {
      if (col.id === columnId) {
        const updatedCol = { ...col, [field]: value }
        
        // If enabling auto-increment, automatically set primary key
        if (field === 'autoIncrement' && value === true) {
          updatedCol.primaryKey = true
          updatedCol.nullable = false // Auto-increment columns cannot be nullable
        }
        
        // If disabling primary key, automatically disable auto-increment
        if (field === 'primaryKey' && value === false && col.autoIncrement) {
          updatedCol.autoIncrement = false
        }
        
        // Validate column name on change
        if (field === 'name' && typeof value === 'string') {
          void validateColumnName(columnId, value)
        }
        
        // Validate length requirement when data type changes
        if (field === 'dataType' && typeof value === 'string') {
          if (['VARCHAR', 'CHAR', 'DECIMAL'].includes(value) && (!col.length || col.length <= 0)) {
            setColumnErrors(prev => ({ 
              ...prev, 
              [`${columnId}_length`]: `${value} columns require a valid length` 
            }))
          } else {
            // Clear length error if not needed
            setColumnErrors(prev => {
              const newErrors = { ...prev }
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { [`${columnId}_length`]: _, ...rest } = newErrors
              return rest
            })
          }
        }
        
        // Validate length when length changes
        if (field === 'length' && ['VARCHAR', 'CHAR', 'DECIMAL'].includes(col.dataType)) {
          if (!value || (typeof value === 'number' && value <= 0)) {
            setColumnErrors(prev => ({ 
              ...prev, 
              [`${columnId}_length`]: `${col.dataType} columns require a valid length` 
            }))
          } else {
            // Clear length error if valid
            setColumnErrors(prev => {
              const newErrors = { ...prev }
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { [`${columnId}_length`]: _, ...rest } = newErrors
              return rest
            })
          }
        }
        
        return updatedCol
      }
      return col
    }))
  }

  const resetForm = useCallback(() => {
    setTableName('')
    setColumns([{
      id: '1',
      name: 'id',
      dataType: 'INT',
      nullable: false,
      primaryKey: true,
      autoIncrement: true,
      unique: false
    }])
    clearValidation()
    setColumnErrors({})
  }, [clearValidation])

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    
    // Validate table name
    const isTableNameValid = await validateForm(
      { tableName },
      { tableName: { required: true, type: 'identifier', maxLength: 64 } }
    )
    
    if (!isTableNameValid.isValid) {
      showToast('error', 'Error', 'Please fix the table name validation errors')
      return
    }

    if (columns.length === 0) {
      showToast('error', 'Error', 'At least one column is required')
      return
    }

    // Validate all column names and data types
    let hasColumnErrors = false
    for (const column of columns) {
      const isValid = validateColumnName(column.id, column.name)
      if (!isValid) {
        hasColumnErrors = true
      }
      
      // Validate that data types requiring length have a valid length
      if (['VARCHAR', 'CHAR', 'DECIMAL'].includes(column.dataType) && (!column.length || column.length <= 0)) {
        setColumnErrors(prev => ({ 
          ...prev, 
          [`${column.id}_length`]: `${column.dataType} columns require a valid length` 
        }))
        hasColumnErrors = true
      } else {
        // Clear length error if valid
        setColumnErrors(prev => {
          const newErrors = { ...prev }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [`${column.id}_length`]: _, ...rest } = newErrors
          return rest
        })
      }
    }

    if (hasColumnErrors) {
      showToast('error', 'Error', 'Please fix the column validation errors')
      return
    }

    // Validate auto-increment columns are primary keys
    const autoIncrementColumns = columns.filter(col => col.autoIncrement)
    const nonPrimaryAutoIncrement = autoIncrementColumns.filter(col => !col.primaryKey)
    if (nonPrimaryAutoIncrement.length > 0) {
      const columnNames = nonPrimaryAutoIncrement.map(col => col.name).join(', ')
      showToast('error', 'Error', `AUTO_INCREMENT columns [${columnNames}] must be defined as primary key`)
      return
    }

    setIsSubmitting(true)
    
    try {
      await AgentProxyService.createTable(connectionId, database, {
        table_name: tableName.trim(),
        schema_name: schema,
        columns: columns.map(col => {
          // Build the type string with length for types that require it
          let typeString = col.dataType
          if (col.length && ['VARCHAR', 'CHAR', 'DECIMAL'].includes(col.dataType)) {
            typeString = `${col.dataType}(${col.length})`
          }
          
          return {
            name: col.name.trim(),
            type: typeString,
            nullable: col.nullable,
            primary_key: col.primaryKey,
            auto_increment: col.autoIncrement,
            default_value: col.defaultValue?.trim() || null
          }
        })
      })

      showToast('success', 'Success', `Table "${tableName}" created successfully`)
      
      // Invalidate all relevant queries to refresh the UI
      await Promise.all([
        // Primary table queries used by DatabaseTree
        queryClient.invalidateQueries({ queryKey: databaseKeys.tables(connectionId, database, schema) }),
        // For databases without schema (MySQL) or when schema is undefined
        queryClient.invalidateQueries({ queryKey: databaseKeys.tables(connectionId, database, undefined) }),
        // Ensure we invalidate all table-related queries for this database
        queryClient.invalidateQueries({ 
          queryKey: databaseKeys.all, 
          predicate: (query) => {
            const key = Array.isArray(query.queryKey) ? (query.queryKey as unknown[]).map((k) => String(k)) : []
            return key.includes('tables') && key.includes(connectionId) && key.includes(database)
          }
        })
      ])
      
      // Small delay to ensure UI has time to process the query invalidations
      await delay(100)
      
      // Call the callback if provided
      onTableCreated?.(tableName.trim())
      
      // Reset form
      resetForm()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create table'
      showToast('error', 'Error', message)
    } finally {
      setIsSubmitting(false)
    }
  }, [tableName, validateForm, columns, validateColumnName, connectionId, database, schema, showToast, queryClient, onTableCreated, resetForm])

  const combinedErrors = useMemo(() => ({...errors, ...columnErrors}), [errors, columnErrors])

  return (
    <div className="h-full bg-white dark:bg-gray-900 p-6 overflow-y-auto">
      <div className="max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Create Table in {schema ? `${database}.${schema}` : database}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Define the structure of your new table with columns, data types, and constraints.
          </p>
        </div>

        {/* Validation Summary */}
        <ValidationSummary 
          errors={combinedErrors} 
          warnings={warnings} 
        />

        {/* Form */}
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
          <FormSection title="Table Configuration">
            <ValidatedInput
              label="Table Name"
              name="tableName"
              value={tableName}
              onChange={useCallback((value: string) => {
                setTableName(value)
                validateTableName(value)
              }, [validateTableName])}
              error={errors.tableName}
              placeholder="Enter table name"
              required
              disabled={isSubmitting}
              helpText="Table name must be unique within the database/schema."
            />
          </FormSection>

          {/* Columns */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Columns *
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Auto-increment columns must be primary keys. At least one column is required.
                </p>
              </div>
              <button
                type="button"
                onClick={addColumn}
                disabled={isSubmitting}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Column
              </button>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Headers */}
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div className="col-span-2">Name *</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-1">Length</div>
                  <div className="col-span-1">NULL</div>
                  <div className="col-span-1">PK</div>
                  <div className="col-span-1">AI</div>
                  <div className="col-span-1">UQ</div>
                  <div className="col-span-2">Default</div>
                  <div className="col-span-1">Actions</div>
                </div>
              </div>

              {/* Column Rows */}
              <div className="max-h-96 overflow-y-auto">
                {columns.map((column) => (
                  <div key={column.id} className="px-4 py-3 border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Name */}
                      <div className="col-span-2">
                        <input
                          type="text"
                          value={column.name}
                          onChange={(e) => { updateColumn(column.id, 'name', e.target.value) }}
                          className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
                            columnErrors[column.id] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Column name"
                          disabled={isSubmitting}
                        />
                        {columnErrors[column.id] && (
                          <p className="text-xs text-red-500 mt-1">{columnErrors[column.id]}</p>
                        )}
                      </div>

                      {/* Type */}
                      <div className="col-span-2">
                        <select
                          value={column.dataType}
                          onChange={(e) => { updateColumn(column.id, 'dataType', e.target.value) }}
                          disabled={isSubmitting}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                        >
                          {DATA_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      {/* Length */}
                      <div className="col-span-1">
                        <input
                          type="number"
                          value={column.length || ''}
                          onChange={(e) => { updateColumn(column.id, 'length', Number.parseInt(e.target.value) || undefined) }}
                          className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white ${
                            columnErrors[`${column.id}_length`] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Length"
                          disabled={isSubmitting}
                        />
                        {columnErrors[`${column.id}_length`] && (
                          <p className="text-xs text-red-500 mt-1">{columnErrors[`${column.id}_length`]}</p>
                        )}
                      </div>

                      {/* Nullable */}
                      <div className="col-span-1 flex justify-center">
                        <input
                          type="checkbox"
                          checked={column.nullable}
                          onChange={(e) => { updateColumn(column.id, 'nullable', e.target.checked) }}
                          disabled={isSubmitting}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>

                      {/* Primary Key */}
                      <div className="col-span-1 flex justify-center">
                        <input
                          type="checkbox"
                          checked={column.primaryKey}
                          onChange={(e) => { updateColumn(column.id, 'primaryKey', e.target.checked) }}
                          disabled={isSubmitting}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>

                      {/* Auto Increment */}
                      <div className="col-span-1 flex justify-center">
                        <input
                          type="checkbox"
                          checked={column.autoIncrement}
                          onChange={(e) => { updateColumn(column.id, 'autoIncrement', e.target.checked) }}
                          disabled={isSubmitting}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>

                      {/* Unique */}
                      <div className="col-span-1 flex justify-center">
                        <input
                          type="checkbox"
                          checked={column.unique}
                          onChange={(e) => { updateColumn(column.id, 'unique', e.target.checked) }}
                          disabled={isSubmitting}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>

                      {/* Default Value */}
                      <div className="col-span-2">
                        <input
                          type="text"
                          value={column.defaultValue || ''}
                          onChange={(e) => { updateColumn(column.id, 'defaultValue', e.target.value) }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                          placeholder="Default value"
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 flex justify-center">
                        {columns.length > 1 && (
                          <button
                            type="button"
                            onClick={() => { removeColumn(column.id) }}
                            disabled={isSubmitting}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <FormActions
            onSubmit={useCallback(() => { void handleSubmit() }, [handleSubmit])}
            onCancel={onCancel || resetForm}
            submitLabel="Create Table"
            isValid={!!tableName.trim() && Object.keys(errors).length === 0 && Object.keys(columnErrors).length === 0}
            isSubmitting={isSubmitting}
          />
        </form>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
            💡 Tips for Creating Tables
          </h3>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <li>• Primary key columns are automatically non-nullable</li>
            <li>• Auto-increment columns must be primary keys and are typically integers</li>
            <li>• VARCHAR columns require a length specification (recommended: 255 for general text)</li>
            <li>• Use appropriate data types: INT for numbers, VARCHAR for text, DATE/DATETIME for dates</li>
            <li>• Consider adding indexes on frequently queried columns (can be done after table creation)</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
